import { useEffect } from "react";

/**
 * Lightweight per-page SEO: updates document title, meta description, canonical,
 * Open Graph and Twitter tags, and optional JSON-LD.
 * No external dependency — fully compatible with React 19.
 */
function upsertMeta(selector, attr, name, content) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

export default function SEO({
  title,
  description,
  canonical,
  image = "https://blackarrowncc.it/black-arrow-logo.png",
  type = "website",
  jsonLd,
}) {
  useEffect(() => {
    if (title) document.title = title;
    if (description) upsertMeta('meta[name="description"]', "name", "description", description);
    if (canonical) upsertLink("canonical", canonical);

    // Open Graph
    upsertMeta('meta[property="og:type"]', "property", "og:type", type);
    if (title) upsertMeta('meta[property="og:title"]', "property", "og:title", title);
    if (description) upsertMeta('meta[property="og:description"]', "property", "og:description", description);
    if (canonical) upsertMeta('meta[property="og:url"]', "property", "og:url", canonical);
    if (image) upsertMeta('meta[property="og:image"]', "property", "og:image", image);

    // Twitter
    if (title) upsertMeta('meta[name="twitter:title"]', "name", "twitter:title", title);
    if (description) upsertMeta('meta[name="twitter:description"]', "name", "twitter:description", description);
    if (image) upsertMeta('meta[name="twitter:image"]', "name", "twitter:image", image);

    // JSON-LD (per-page)
    const existing = document.getElementById("seo-page-jsonld");
    if (existing) existing.remove();
    if (jsonLd) {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.id = "seo-page-jsonld";
      script.text = JSON.stringify(jsonLd);
      document.head.appendChild(script);
    }

    return () => {
      const s = document.getElementById("seo-page-jsonld");
      if (s) s.remove();
    };
  }, [title, description, canonical, image, type, jsonLd]);

  return null;
}
